import { useCallback, useEffect, useMemo, useState } from 'react'
import { ROW_HEIGHT } from '../../../containers/BulkSheet'
import uiStates, {
  UiStateKey,
  UiStateScope,
} from '../../../../lib/commons/uiStates'
import {
  AccountCategory,
  RowFilterKey,
  RowFilterKeyType,
  RowStyle,
  RowStyleType,
} from '../profitLossItems'
import { getFinancialYear } from '../../../../utils/date'
import store from '../../../../store'

export type StoredPageState = {
  startDate: string
  endDate: string
  rowHeight: ROW_HEIGHT
  accountCategory: string | undefined
  rowFilterTypes?: RowFilterKeyType[]
  rowStyle: RowStyleType | undefined
}

export const usePageState = (
  functionUuid
): {
  startDate: string
  setStartDate: (_: string) => void
  endDate: string
  setEndDate: (_: string) => void
  rowHeight: ROW_HEIGHT
  setRowHeight: (_: ROW_HEIGHT) => void
  accountCategory: string | undefined
  setAccountCategory: (_: string | undefined) => void
  rowFilterTypes?: RowFilterKeyType[]
  setRowFilterTypes?: (_: RowFilterKeyType[]) => void
  rowStyle: RowStyleType | undefined
  setRowStyle: (_: RowStyleType | undefined) => void
  initialized: boolean
} => {
  const organization = useMemo(() => store.getState()?.tenant.organization, [])
  const [startDate, setStartDate] = useState<string>(
    getFinancialYear(organization).formatForApi()!
  )
  const [endDate, setEndDate] = useState<string>(
    getFinancialYear(organization).addMonths(11).formatForApi()!
  )
  const [rowHeight, setRowHeight] = useState<ROW_HEIGHT>(ROW_HEIGHT.SMALL)
  const [accountCategory, setAccountCategory] = useState<string | undefined>(
    AccountCategory.OperatingRevenue
  )
  const [rowFilterTypes, setRowFilterTypes] = useState<RowFilterKeyType[]>(
    Object.values(RowFilterKey)
  )
  const [rowStyle, setRowStyle] = useState<RowStyleType | undefined>(
    RowStyle.Tree
  )
  const [initialized, setInitialized] = useState(false)

  const restorePageState = useCallback(async () => {
    const response = await uiStates.get({
      applicationFunctionUuid: functionUuid,
      key: UiStateKey.PageState,
      scope: UiStateScope.User,
    })
    const stored = response.json
    try {
      if (!stored.value) return
      const payload: StoredPageState = JSON.parse(stored.value)
      payload.rowHeight && setRowHeight(payload.rowHeight)
      payload.startDate && setStartDate(payload.startDate)
      payload.endDate && setEndDate(payload.endDate)
      payload.accountCategory && setAccountCategory(payload.accountCategory)
      payload.rowFilterTypes && setRowFilterTypes(payload.rowFilterTypes)
      payload.rowStyle && setRowStyle(payload.rowStyle)
    } finally {
      setInitialized(true)
    }
  }, [functionUuid])

  const rememberPageState = useCallback(
    (value: StoredPageState) => {
      uiStates.update(
        {
          key: UiStateKey.PageState,
          scope: UiStateScope.User,
          value: JSON.stringify(value),
        },
        functionUuid
      )
    },
    [functionUuid]
  )

  useEffect(() => {
    restorePageState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!initialized) return
    rememberPageState({
      startDate,
      endDate,
      rowHeight,
      accountCategory,
      rowFilterTypes,
      rowStyle,
    })
  }, [
    accountCategory,
    endDate,
    initialized,
    rememberPageState,
    rowHeight,
    startDate,
    rowFilterTypes,
    rowStyle,
  ])

  return {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    rowHeight,
    setRowHeight,
    accountCategory,
    setAccountCategory,
    rowFilterTypes,
    setRowFilterTypes,
    rowStyle,
    setRowStyle,
    initialized,
  }
}
